import React, { useCallback, useEffect } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import { mediaQueryUp } from 'components/utils/breakpoint'

const ToggleButtonsContainer = styled(Group)`
  padding: ${rem(8)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const ToggleButton = styled(Clickable)`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${rem(44)};
  padding: ${rem(4)} ${rem(16)};
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  transition: background-color 0.2s, box-shadow 0.2s, color 0.2s, border-color 0.2s;
  outline-offset: 1px;

  ${mediaQueryUp.tablet} {
    flex: initial;
    min-width: ${rem(160)};
  }

  &:focus {
    ${interactiveFocusStyle}
  }

  &.is-selected {
    background-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: ${props => props.theme.shadow.flat.small};
  }

  &:not(.is-selected):not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.default.six};
      }
    }
  }
`

interface Props {
  flightsAvailable: boolean;
  flights: boolean;
  isMemberPrice?: boolean;
  hotelTotalPrice: number;
  hotelFlightTotalPrice?: number;
  pricesLoading?: boolean;
}

function BaseHotelFlightsToggleButton(props: Props) {
  const { flightsAvailable, flights, isMemberPrice, hotelTotalPrice, hotelFlightTotalPrice, pricesLoading } = props
  const dispatch = useOfferPageDispatch()

  const noFlightsPrice = hotelTotalPrice === hotelFlightTotalPrice
  const disableFlights = !flightsAvailable || noFlightsPrice

  const onSelectHotel = useCallback(() => {
    if (flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    }
  }, [flights, dispatch])

  const onSelectHotelFlights = useCallback(() => {
    if (!flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: true })
    }
  }, [flights, dispatch])

  useEffect(() => {
    if (!disableFlights && flights) {
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableFlights])

  return (
    <ToggleButtonsContainer direction="horizontal" gap={4}>
      <ToggleButton className={cn({ 'is-selected': !flights })} onClick={onSelectHotel}>
        <Group direction="vertical" horizontalAlign="center">
          <BodyText
            variant="medium"
            weight={!flights ? 'bold' : undefined}
            colour={!flights ? 'neutral-eight' : 'neutral-one'}
          >
            Hotel
          </BodyText>
          {!pricesLoading && hotelTotalPrice > 0 && <BodyText variant="small" colour={flights ? 'neutral-three' : 'neutral-five'}>{isMemberPrice && 'From '}<FormatCurrency format="roundedDollar" value={hotelTotalPrice} /></BodyText>}
        </Group>
      </ToggleButton>
      <ToggleButton className={cn({ 'is-selected': flights })} disabled={disableFlights} onClick={onSelectHotelFlights}>
        <Group direction="vertical" horizontalAlign="center">
          <BodyTextBlock
            variant="medium"
            weight={flights ? 'bold' : undefined}
            colour={flights ? 'neutral-eight' : 'neutral-one'}
            startIcon={<LinePlaneDepartureIcon />}
          >
            Hotel + flights
          </BodyTextBlock>
          {!pricesLoading && !disableFlights && !!hotelFlightTotalPrice && <BodyText variant="small" colour={flights ? 'neutral-five' : 'neutral-three'}>{isMemberPrice && 'From '}<FormatCurrency format="roundedDollar" value={hotelFlightTotalPrice} /></BodyText>}
          {!pricesLoading && disableFlights && !flightsAvailable && <BodyText variant="small" colour={flights ? 'neutral-five' : 'neutral-three'}>Unavailable for your dates</BodyText>}
        </Group>
      </ToggleButton>
    </ToggleButtonsContainer>
  )
}

export default withOfferPageAndReduxState((state) => ({
  flights: state.flights,
}), BaseHotelFlightsToggleButton)
